import { Injectable } from '@angular/core';
import type { ClientRenderSurface } from './bridge/client-render-runtime-bridge.service';

export type RenderOutputPreset = 'small' | 'balanced' | 'high';
export type RenderOutputSource = 'auto' | 'command' | 'job' | 'user';
export type RenderOutputContentKind = 'generated-image-sequence' | 'template' | 'source-video' | 'mixed';

export const RENDER_OUTPUT_PRESET_OPTIONS: { value: RenderOutputPreset; label: string }[] = [
  { value: 'small', label: 'Small' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'high', label: 'High' }
];

// ---------------------------------------------------------------------------
// Long-render adaptive policy — thresholds and reduction factors.
// When a render exceeds LONG_RENDER_THRESHOLD_SECONDS the browser memory
// budget for a 1080p real-time encode becomes tight, so we apply two levers:
//   • Cap FPS at LONG_RENDER_FPS_CAP (24) to reduce frame throughput.
//   • Scale bitrate down by LONG_RENDER_BITRATE_FACTOR (0.65) with a
//     sensible floor (LONG_RENDER_BITRATE_FLOOR_BPS).
// Shorts and renders at or below the threshold are UNCHANGED.
// ---------------------------------------------------------------------------
const LONG_RENDER_THRESHOLD_SECONDS = 600;     // >10 min triggers adaptive mode
const LONG_RENDER_FPS_CAP            = 24;      // cap fps for long 16:9 mains
const LONG_RENDER_BITRATE_FACTOR     = 0.65;    // ~65% of normal bitrate
const LONG_RENDER_BITRATE_FLOOR_BPS  = 800_000; // never below 0.8 Mbps

export interface RenderOutputSettings {
  preset: RenderOutputPreset;
  source: RenderOutputSource;
  width?: number | null;
  height?: number | null;
  fps: number;
  targetBitrate: number;
  estimatedSizeMb: number;
}

export interface RenderOutputPolicyRequest {
  surface: ClientRenderSurface;
  durationSeconds?: number | null;
  aspectRatio?: '9:16' | '16:9' | string | null;
  width?: number | null;
  height?: number | null;
  preset?: RenderOutputPreset | string | null;
  source?: RenderOutputSource | string | null;
  contentKind?: RenderOutputContentKind | null;
  existing?: unknown;
}

const GENERATED_SHORT_BITRATES: Record<RenderOutputPreset, number> = {
  small: 2_500_000,
  balanced: 3_500_000,
  high: 5_500_000
};

const GENERATED_LONG_BITRATES: Record<RenderOutputPreset, number> = {
  small: 1_350_000,
  balanced: 2_000_000,
  high: 3_200_000
};

const SOURCE_VIDEO_SHORT_BITRATES: Record<RenderOutputPreset, number> = {
  small: 3_500_000,
  balanced: 5_000_000,
  high: 7_000_000
};

const SOURCE_VIDEO_LONG_BITRATES: Record<RenderOutputPreset, number> = {
  small: 2_200_000,
  balanced: 3_000_000,
  high: 4_500_000
};

@Injectable({ providedIn: 'root' })
export class RenderOutputPolicyService {
  resolve(request: RenderOutputPolicyRequest): RenderOutputSettings {
    const existing = this.normalizeExisting(request.existing);
    const preset = this.normalizePreset(existing?.preset ?? request.preset);
    const source = this.normalizeSource(existing?.source ?? request.source);
    const durationSeconds = this.safeDuration(request.durationSeconds);

    // Adaptive long-render policy: long 16:9 main renders (> LONG_RENDER_THRESHOLD_SECONDS)
    // cap fps and reduce bitrate to stay within browser memory bounds.
    // Shorts (9:16) and renders at/below the threshold are UNCHANGED.
    const isLongMain = durationSeconds > LONG_RENDER_THRESHOLD_SECONDS
      && request.aspectRatio !== '9:16';

    const baseFps = this.safeInt(existing?.fps, 30, 12, 60);
    const fps = isLongMain ? Math.min(baseFps, LONG_RENDER_FPS_CAP) : baseFps;

    const width = this.safeNullableInt(request.width);
    const height = this.safeNullableInt(request.height);
    const normalBitrate = this.resolveTargetBitrate(request.surface, preset, durationSeconds, request.contentKind);
    const resolvedBitrate = isLongMain
      ? Math.max(LONG_RENDER_BITRATE_FLOOR_BPS, Math.round(normalBitrate * LONG_RENDER_BITRATE_FACTOR))
      : normalBitrate;
    const bitrate = this.safeInt(
      existing?.targetBitrate,
      resolvedBitrate,
      800_000,
      8_000_000
    );

    return {
      preset,
      source,
      width,
      height,
      fps,
      targetBitrate: bitrate,
      estimatedSizeMb: this.estimateSizeMb(bitrate, durationSeconds)
    };
  }

  fromMetadata(
    metadata: Record<string, unknown> | null | undefined,
    fallback: Omit<RenderOutputPolicyRequest, 'existing'>
  ): RenderOutputSettings {
    return this.resolve({
      ...fallback,
      existing: metadata?.['renderOutput']
    });
  }

  toMetadata(settings: RenderOutputSettings): RenderOutputSettings {
    return {
      preset: settings.preset,
      source: settings.source,
      width: settings.width ?? null,
      height: settings.height ?? null,
      fps: settings.fps,
      targetBitrate: settings.targetBitrate,
      estimatedSizeMb: settings.estimatedSizeMb
    };
  }

  private resolveTargetBitrate(
    surface: ClientRenderSurface,
    preset: RenderOutputPreset,
    durationSeconds: number,
    contentKind?: RenderOutputContentKind | null
  ): number {
    const sourceVideo = contentKind === 'source-video'
      || contentKind === 'mixed'
      || surface === 'review-video'
      || surface === 'processing-video';
    const shortMap = sourceVideo ? SOURCE_VIDEO_SHORT_BITRATES : GENERATED_SHORT_BITRATES;
    const longMap = sourceVideo ? SOURCE_VIDEO_LONG_BITRATES : GENERATED_LONG_BITRATES;
    const short = shortMap[preset];
    const long = longMap[preset];

    if (durationSeconds <= 120) return short;
    if (durationSeconds >= 1800) return long;

    const ratio = (durationSeconds - 120) / (1800 - 120);
    return Math.round(short + (long - short) * ratio);
  }

  private estimateSizeMb(targetBitrate: number, durationSeconds: number): number {
    const megabytes = (targetBitrate * durationSeconds) / 8 / 1_000_000;
    return Math.round(megabytes * 10) / 10;
  }

  private normalizeExisting(value: unknown): Partial<RenderOutputSettings> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Partial<RenderOutputSettings>
      : null;
  }

  private normalizePreset(value: unknown): RenderOutputPreset {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'small' || normalized === 'high' ? normalized : 'balanced';
  }

  private normalizeSource(value: unknown): RenderOutputSource {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'command' || normalized === 'job' || normalized === 'user'
      ? normalized
      : 'auto';
  }

  private safeDuration(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 120;
  }

  private safeNullableInt(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : null;
  }

  private safeInt(value: unknown, fallback: number, min: number, max: number): number {
    const parsed = Number(value);
    const next = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
    return Math.max(min, Math.min(max, next));
  }
}
